/**
 * usePositionLevelMutations Hook
 *
 * 직위(Position Level) 데이터의 생성/수정/삭제(CUD) 작업을 처리하는 Hook입니다.
 *
 * **기능:**
 * - 직위 생성 (createPositionLevel)
 * - 직위 수정 (updatePositionLevel)
 * - 직위 삭제 (deletePositionLevel - 소프트 삭제)
 *
 * **사용 예시:**
 * ```tsx
 * const { createPositionLevel, updatePositionLevel, deletePositionLevel } =
 *   usePositionLevelMutations();
 *
 * // 직위 생성
 * await createPositionLevel({
 *   name: '과장',
 *   code: 'MANAGER',
 *   rankWeight: 3,
 *   requiredRecognizedYearsFromHire: 9
 * });
 *
 * // 직위 수정
 * await updatePositionLevel('pos_manager', {
 *   name: '과장(수정)',
 *   requiredRecognizedYearsFromHire: 10
 * });
 *
 * // 직위 삭제 (소프트 삭제)
 * await deletePositionLevel('pos_manager');
 * ```
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from './useAuthContext';
import {
  CreatePositionLevelInput,
  UpdatePositionLevelInput,
  mapPositionLevelToFirestore,
} from '../types/positionLevel';
import { invalidateCache, CACHE_KEY } from '../utils/masterDataCache';

/**
 * usePositionLevelMutations Hook 반환 타입
 */
export interface UsePositionLevelMutationsResult {
  /** 직위 생성 함수 */
  createPositionLevel: (input: CreatePositionLevelInput) => Promise<string>;

  /** 직위 수정 함수 */
  updatePositionLevel: (id: string, input: UpdatePositionLevelInput) => Promise<void>;

  /** 직위 삭제 함수 */
  deletePositionLevel: (id: string) => Promise<void>;
}

/**
 * 직위 CRUD Hook
 *
 * @returns UsePositionLevelMutationsResult
 */
export function usePositionLevelMutations(): UsePositionLevelMutationsResult {
  const authContext = useAuthContext();

  /**
   * 직위 생성
   *
   * 새로운 직위를 Firestore에 추가합니다.
   *
   * **자동 처리:**
   * - order: 미입력 시 기존 최대값 + 1
   * - status: 미입력 시 'active'
   * - createdBy, updatedBy: 현재 로그인 사용자
   * - createdAt, updatedAt: 서버 타임스탬프
   *
   * @param input 직위 생성 입력
   * @returns 생성된 직위 ID
   * @throws 로그인하지 않은 경우 또는 Firestore 오류
   */
  const createPositionLevel = async (input: CreatePositionLevelInput): Promise<string> => {
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    // 1. order 자동 계산 (미입력 시)
    let order = input.order;
    if (order === undefined) {
      order = await calculateNextOrder();
    }

    // 2. 직위 ID 생성 (자동 생성 또는 커스텀)
    const positionLevelId = generatePositionLevelId(input.name, input.code);

    // 3. Firestore 문서 데이터 생성
    const docData = mapPositionLevelToFirestore({
      name: input.name,
      code: input.code,
      order,
      rankWeight: input.rankWeight,
      requiredRecognizedYearsFromHire: input.requiredRecognizedYearsFromHire ?? null,
      status: input.status || 'active',
      description: input.description,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
      updatedBy: authContext.userId,
    });

    // 4. Firestore에 저장
    const docRef = doc(db, 'positionLevels', positionLevelId);
    await setDoc(docRef, docData);

    // V259: 캐시 무효화
    invalidateCache(CACHE_KEY.POSITION_LEVELS);
    return positionLevelId;
  };

  /**
   * 직위 수정
   *
   * 기존 직위 정보를 수정합니다.
   *
   * **자동 처리:**
   * - updatedBy: 현재 로그인 사용자
   * - updatedAt: 서버 타임스탬프
   *
   * @param id 직위 ID
   * @param input 수정할 필드
   * @throws 직위가 존재하지 않는 경우 또는 Firestore 오류
   */
  const updatePositionLevel = async (
    id: string,
    input: UpdatePositionLevelInput
  ): Promise<void> => {
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    // 1. 기존 문서 존재 여부 확인
    const docRef = doc(db, 'positionLevels', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(`직위를 찾을 수 없습니다: ${id}`);
    }

    // 2. Firestore 문서 데이터 생성 (제공된 필드만 포함)
    const docData = mapPositionLevelToFirestore({
      ...input,
      updatedAt: serverTimestamp() as any,
      updatedBy: authContext.userId,
    });

    // 3. Firestore 문서 업데이트
    await updateDoc(docRef, docData);

    // V259: 캐시 무효화
    invalidateCache(CACHE_KEY.POSITION_LEVELS);
  };

  /**
   * 직위 삭제 (소프트 삭제)
   *
   * 직위를 삭제합니다. 기본적으로 소프트 삭제(status='inactive')를 사용합니다.
   *
   * **소프트 삭제:**
   * - status를 'inactive'로 변경
   * - 문서는 유지됨 (히스토리 보존)
   * - 직위 목록에서 필터링으로 제외 가능
   * - 데이터 복구 가능
   *
   * **주의:**
   * - 해당 직위를 사용 중인 직원이 있어도 삭제 가능 (소프트 삭제이므로)
   * - 실제 문서 삭제는 하지 않음 (향후 필요 시 하드 삭제 기능 추가 가능)
   *
   * @param id 직위 ID
   * @throws 직위가 존재하지 않는 경우 또는 Firestore 오류
   */
  const deletePositionLevel = async (id: string): Promise<void> => {
    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    // 1. 기존 문서 존재 여부 확인
    const docRef = doc(db, 'positionLevels', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(`직위를 찾을 수 없습니다: ${id}`);
    }

    // 2. 소프트 삭제 (status를 'inactive'로 변경)
    await updateDoc(docRef, {
      status: 'inactive',
      updatedAt: serverTimestamp(),
      updatedBy: authContext.userId,
    });

    // V259: 캐시 무효화
    invalidateCache(CACHE_KEY.POSITION_LEVELS);
  };

  return {
    createPositionLevel,
    updatePositionLevel,
    deletePositionLevel,
  };
}

/**
 * 다음 order 값 계산
 *
 * 기존 직위 중 가장 큰 order 값을 찾아 +1 반환합니다.
 * 직위가 하나도 없으면 1을 반환합니다.
 *
 * @returns 다음 order 값
 */
async function calculateNextOrder(): Promise<number> {
  if (!db) {
    return 1;
  }

  try {
    // 기존 직위 목록 조회 (order 내림차순 정렬)
    const positionLevelsCollection = collection(db, 'positionLevels');
    const q = query(positionLevelsCollection, orderBy('order', 'desc'));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 1; // 직위가 하나도 없으면 1
    }

    // 가장 큰 order 값 + 1
    const maxOrder = snapshot.docs[0].data().order || 0;
    return maxOrder + 1;
  } catch (err) {
    console.error('order 계산 실패:', err);
    return 1;
  }
}

/**
 * 직위 ID 생성
 *
 * 직위명과 코드를 기반으로 Firestore 문서 ID를 생성합니다.
 *
 * **생성 규칙:**
 * - code가 있으면: pos_{code_lowercase}
 * - code가 없으면: pos_{name_slug}_{timestamp}
 *
 * **예시:**
 * - 과장, MANAGER → pos_manager
 * - 사원, (코드 없음) → pos_사원_abc123
 *
 * @param name 직위명
 * @param code 시스템 코드 (선택)
 * @returns Firestore 문서 ID
 */
function generatePositionLevelId(name: string, code?: string): string {
  if (code) {
    // 코드가 있으면 코드 기반 ID 생성
    return `pos_${code.toLowerCase()}`;
  }

  // 코드가 없으면 직위명 기반 ID 생성
  const nameSlug = name
    .toLowerCase()
    .replace(/\s+/g, '_') // 공백을 언더스코어로
    .replace(/[^a-z0-9_가-힣]/g, '') // 영문/숫자/언더스코어/한글만 허용
    .slice(0, 20); // 최대 20자

  const timestamp = Date.now().toString(36); // 타임스탬프 (36진수)
  return `pos_${nameSlug}_${timestamp}`;
}

/**
 * ============================================
 * TODO: 하드 삭제 기능 (향후 필요 시 구현)
 * ============================================
 *
 * 현재는 소프트 삭제만 지원합니다.
 * 실제 문서를 삭제해야 하는 경우:
 *
 * ```ts
 * const hardDeletePositionLevel = async (id: string): Promise<void> => {
 *   if (!authContext.userId) {
 *     throw new Error('로그인이 필요합니다.');
 *   }
 *
 *   if (!db) {
 *     throw new Error('Firestore가 초기화되지 않았습니다.');
 *   }
 *
 *   // 1. 해당 직위를 사용 중인 직원이 있는지 확인
 *   const employeesCollection = collection(db, 'users');
 *   const q = query(employeesCollection, where('positionTitle', '==', name));
 *   const snapshot = await getDocs(q);
 *
 *   if (!snapshot.empty) {
 *     throw new Error('해당 직위를 사용 중인 직원이 있어 삭제할 수 없습니다.');
 *   }
 *
 *   // 2. 실제 문서 삭제
 *   const docRef = doc(db, 'positionLevels', id);
 *   await deleteDoc(docRef);
 *
 *   console.log('✅ 직위 삭제 완료 (하드 삭제):', id);
 * };
 * ```
 *
 * **주의:**
 * - 하드 삭제는 데이터 복구가 불가능하므로 신중하게 사용
 * - 직원 데이터와의 참조 무결성 확인 필요
 * - SuperAdmin만 사용할 수 있도록 권한 체크 권장
 */

/**
 * ============================================
 * TODO: 직위 순서 재정렬 기능 (향후 구현)
 * ============================================
 *
 * 직위 목록 화면에서 드래그&드롭으로 순서를 변경하는 기능:
 *
 * ```ts
 * const reorderPositionLevels = async (
 *   positionLevelIds: string[]
 * ): Promise<void> => {
 *   if (!authContext.userId) {
 *     throw new Error('로그인이 필요합니다.');
 *   }
 *
 *   if (!db) {
 *     throw new Error('Firestore가 초기화되지 않았습니다.');
 *   }
 *
 *   // Firestore Batch를 사용하여 여러 문서를 한 번에 업데이트
 *   const batch = writeBatch(db);
 *
 *   positionLevelIds.forEach((id, index) => {
 *     const docRef = doc(db, 'positionLevels', id);
 *     batch.update(docRef, {
 *       order: index + 1,
 *       updatedAt: serverTimestamp(),
 *       updatedBy: authContext.userId
 *     });
 *   });
 *
 *   await batch.commit();
 *   console.log('✅ 직위 순서 재정렬 완료');
 * };
 * ```
 *
 * **UI 구현:**
 * - react-beautiful-dnd 또는 dnd-kit 라이브러리 사용
 * - 드래그&드롭으로 순서 변경 → reorderPositionLevels() 호출
 * - 변경 즉시 Firestore에 반영
 */

/**
 * ============================================
 * ID 생성 전략 (향후 개선 방안)
 * ============================================
 *
 * **현재 방식:**
 * - code가 있으면: pos_{code} (예: pos_manager)
 * - code가 없으면: pos_{name}_{timestamp} (예: pos_사원_abc123)
 *
 * **문제점:**
 * - code 기반 ID는 중복 가능성 있음 (같은 코드로 재생성 시)
 * - 한글 이름은 URL/ID로 사용 시 가독성 떨어짐
 *
 * **개선 방안 1: Auto-ID 사용**
 * ```ts
 * const docRef = doc(collection(db, 'positionLevels')); // 자동 ID 생성
 * const positionLevelId = docRef.id; // 예: 1a2b3c4d5e6f
 * await setDoc(docRef, docData);
 * ```
 * - 장점: 중복 걱정 없음
 * - 단점: ID가 의미 없는 랜덤 문자열 (디버깅 어려움)
 *
 * **개선 방안 2: 중복 체크 후 생성**
 * ```ts
 * async function generateUniquePositionLevelId(
 *   name: string,
 *   code?: string
 * ): Promise<string> {
 *   let baseId = code ? `pos_${code.toLowerCase()}` : `pos_${slugify(name)}`;
 *   let id = baseId;
 *   let counter = 1;
 *
 *   while (true) {
 *     const docRef = doc(db, 'positionLevels', id);
 *     const docSnap = await getDoc(docRef);
 *
 *     if (!docSnap.exists()) {
 *       return id; // 중복 없으면 사용
 *     }
 *
 *     // 중복이면 번호 추가
 *     id = `${baseId}_${counter}`;
 *     counter++;
 *   }
 * }
 * ```
 * - 장점: 의미 있는 ID + 중복 방지
 * - 단점: 추가 Firestore 읽기 비용
 *
 * **권장:**
 * - 직위 마스터는 생성 빈도가 낮으므로 현재 방식 유지
 * - 중복 발생 시 에러 처리로 사용자에게 안내
 */
